/**
 * Distributor Service
 * Handles all distributor related operations
 */

import { apiRequest } from '../utils/apiResponseHandler';
import { getApiUrl } from '../config/api';

const distributorService = {
  // ==================== DISTRIBUTOR MANAGEMENT ====================

  // List Distributors
  async getDistributors(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const url = queryParams.toString()
      ? `${getApiUrl('DISTRIBUTORS')}?${queryParams.toString()}`
      : getApiUrl('DISTRIBUTORS');

    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Get Distributor by ID
  async getDistributorById(distributorId) {
    return await apiRequest(getApiUrl('DISTRIBUTOR_BY_ID', distributorId), {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // Create Distributor
  async createDistributor(distributorData) {
    return await apiRequest(getApiUrl('DISTRIBUTORS'), {
      method: 'POST',
      body: distributorData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Update Distributor
  async updateDistributor(distributorId, distributorData) {
    return await apiRequest(getApiUrl('DISTRIBUTOR_BY_ID', distributorId), {
      method: 'PUT',
      body: distributorData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Partially Update Distributor
  async patchDistributor(distributorId, distributorData) {
    return await apiRequest(getApiUrl('DISTRIBUTOR_BY_ID', distributorId), {
      method: 'PATCH',
      body: distributorData,
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Delete Distributor
  async deleteDistributor(distributorId) {
    return await apiRequest(getApiUrl('DISTRIBUTOR_BY_ID', distributorId), {
      method: 'DELETE',
      showSuccessMessage: true,
      showErrorMessage: true
    });
  },

  // Get External View (Public API for distributors)
  async getExternalView(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const url = queryParams.toString()
      ? `${getApiUrl('EXTERNAL_VIEW')}?${queryParams.toString()}`
      : getApiUrl('EXTERNAL_VIEW');

    return await apiRequest(url, {
      method: 'GET',
      showSuccessMessage: false,
      showErrorMessage: true
    });
  },

  // ==================== UTILITY FUNCTIONS ====================

  // Validate Distributor Data
  validateDistributorData(distributorData) {
    const errors = {};

    if (!distributorData.name?.trim()) {
      errors.name = 'Distributor name is required';
    }

    if (distributorData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(distributorData.email)) {
      errors.email = 'Invalid email format';
    }

    if (distributorData.website && !/^https?:\/\/.+/.test(distributorData.website)) {
      errors.website = 'Invalid website URL format';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Format Distributor Data for API
  formatDistributorDataForAPI(distributorData) {
    return {
      name: distributorData.name?.trim(),
      contact_person: distributorData.contact_person?.trim() || '',
      email: distributorData.email?.trim() || '',
      phone: distributorData.phone?.trim() || '',
      address: distributorData.address?.trim() || '',
      website: distributorData.website?.trim() || '',
      is_active: distributorData.is_active !== false
    };
  },

  // Get Distributor Status Badge
  getDistributorStatusBadge(distributor) {
    if (!distributor.is_active) {
      return { text: 'Inactive', class: 'inactive', color: '#e53e3e' };
    }

    return { text: 'Active', class: 'active', color: '#38a169' };
  },

  // Search Distributors by Name
  searchByName(distributors, searchTerm) {
    return distributors.filter(distributor =>
      distributor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (distributor.contact_person && distributor.contact_person.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  },

  // Get Distributor Statistics
  getDistributorStats(distributors) {
    const total = distributors.length;
    const active = distributors.filter(d => d.is_active).length;
    const inactive = total - active;

    return {
      total,
      active,
      inactive
    };
  },

  // Format Distributor Data for Display
  formatDistributorData(distributor) {
    return {
      ...distributor,
      status_badge: this.getDistributorStatusBadge(distributor),
      formatted_created_at: distributor.created_at ?
        new Date(distributor.created_at).toLocaleDateString() : 'Unknown',
      formatted_updated_at: distributor.updated_at ?
        new Date(distributor.updated_at).toLocaleDateString() : 'Unknown'
    };
  }
};

export { distributorService };
export default distributorService;
